/**
 * Parse API price values that may be numeric OR already abbreviated
 * (e.g. "₹3.2 Cr", "89 L", "50 K", "3,20,00,000").
 * Returns a finite number or null.
 */
export const parseIndianAmount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  let raw = String(value).trim();
  if (!raw || raw.toUpperCase() === "NA" || raw.toUpperCase() === "N/A") {
    return null;
  }

  // Strip currency markers / grouping; keep decimal + optional Cr/L/K unit
  raw = raw
    .replace(/₹/g, "")
    .replace(/Rs\.?/gi, "")
    .replace(/,/g, "")
    .replace(/\s+/g, "");

  const match = raw.match(/^(-?\d+(?:\.\d+)?)(cr|crore|crores|l|lac|lacs|lakh|lakhs|k|thousand)?$/i);
  if (!match) return null;

  const amount = Number(match[1]);
  if (!Number.isFinite(amount)) return null;

  const unit = (match[2] || "").toLowerCase();
  if (!unit) return amount;
  if (unit === "cr" || unit === "crore" || unit === "crores") return amount * 10000000;
  if (unit === "l" || unit === "lac" || unit === "lacs" || unit === "lakh" || unit === "lakhs") {
    return amount * 100000;
  }
  if (unit === "k" || unit === "thousand") return amount * 1000;

  return null;
};

/**
 * Prefer the first parseable price-like field on a property object.
 * Admin stores TotalPrice → OriginalPropertyValue / marketupdate.MarketPrice.
 */
export const resolvePropertyPrice = (property) => {
  if (!property || typeof property !== "object") return null;

  const candidates = [
    property.MarketPrice,
    property.latestMarketPrice,
    property.LatestMarketPrice,
    property.CurrentValue,
    property.OriginalPropertyValue,
    property.TotalPrice,
    property.total_price,
    property["Total Price"],
    property.Price,
    property.property_value,
  ];

  for (const candidate of candidates) {
    const num = parseIndianAmount(candidate);
    if (num !== null) return num;
  }
  return null;
};

/** Full Indian grouping: 32000000 → "3,20,00,000" */
export const formatIndianCurrency = (value) => {
  const num = parseIndianAmount(value);
  if (num === null) return null;
  return num.toLocaleString("en-IN");
};

export const formatIndianCurrencyWithSymbol = (value) => {
  const formatted = formatIndianCurrency(value);
  return formatted ? `₹ ${formatted}` : null;
};
